import React, { useState, useEffect } from 'react';
import { Box, Typography} from '@mui/material';
import { styled } from '@mui/system';

// Permission options
const PERMISSIONS = [
  { id: 'product_upload', label: 'Product Upload' },
  { id: 'show_orders', label: 'Show Orders' },
  { id: 'show_inquiries', label: 'Show Inquiries' },
  { id: 'download_report', label: 'Download Report' },
  { id: 'show_user_list', label: 'Show User List' }
];

  const SpinningLogo = styled("img")(({ theme }) => ({
    width: "150px",
    height: "150px",
  }));


// Converts an API role name (e.g. "INQUIRY_ADMIN") into a display label ("Inquiry Admin")
const formatRoleName = (roleName = '') =>
  roleName
    .split('_')
    .filter(Boolean)
    .map(word => word.charAt(0) + word.slice(1).toLowerCase())
    .join(' ');

const AllAdmins = () => {
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    countryCode: '',
    countryId: null,
    gender: '',
    role: '',
    password: '',
    permissions: []
  });

  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState([]); // [{ roleId, roleName, displayName }] fetched from API
  const [countries, setCountries] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null); // which admin to delete
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  // Fetch admins from API
  useEffect(() => {
    fetchAdmins();
    fetchCountries();
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const res = await fetch('https://car.amar.co.jp/api/authorization/admin-user/get-all-roles', {
        headers: { 'accept': 'application/json', 'Accept-Language': 'en' }
      });
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data.roles || data.roleList || data.data || []);

      // Only show admin-side roles in this screen (exclude the plain "USER" role)
      const adminRoles = list
        .filter(r => r.roleName !== 'USER')
        .map(r => ({
          roleId: r.roleId,
          roleName: r.roleName,
          displayName: formatRoleName(r.roleName)
        }));

      setRoles(adminRoles);
    } catch (err) {
      console.error('Failed to fetch roles:', err);
    }
  };

  const fetchCountries = async () => {
    try {
      const res = await fetch('https://car.amar.co.jp/api/authorization/shipping/all-countries', {
        headers: { 'Accept-Language': 'en' }
      });
      const data = await res.json();
      setCountries(data); // [{ countryId, countryName, countryPhoneCode, isoCode }]
    } catch (err) {
      console.error('Failed to fetch countries:', err);
    }
  };

  const fetchAdmins = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('https://car.amar.co.jp/api/authorization/admin-user/get-all-admin-superAdmin', {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Accept-Language': 'en'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch admins');
      }

      const data = await response.json();
      
      if (data.code === 200 && data.userList) {
        const transformedAdmins = data.userList.map(user => ({
          id: user.userId,
          name: `${user.firstName} ${user.lastName}`.trim(),
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.userEmailId,
          phone: user.userMobileNumber,
          countryCode: user.countryCode,
          gender: user.gender,
          role: user.roleName,
          displayRole: formatRoleName(user.roleName),
          lastActive: new Date().toISOString(),
          status: 'active',
          permissions: PERMISSIONS.map(p => p.id),
          isSuperAdmin: user.roleName === 'SUPER_ADMIN'
        }));
        
        setAdmins(transformedAdmins);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching admins:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredAdmins = admins.filter(admin => {
    const matchesSearch = admin.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         admin.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         admin.displayRole.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTab = activeTab === 'all' || admin.status === activeTab;
    return matchesSearch && matchesTab;
  });

  // Column sorting (click a header) — matches the pattern used in
  // AllTickets.js / AllUsers.js.
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <span style={{ marginLeft: '4px', fontSize: '10px', color: 'white' }}>▲▼</span>;
    return <span style={{ marginLeft: '4px', fontSize: '11px', color: 'white' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>;
  };

  const sortedFilteredAdmins = !sortField ? filteredAdmins : [...filteredAdmins].sort((a, b) => {
    if (sortField === 'id') {
      return sortDirection === 'asc' ? a.id - b.id : b.id - a.id;
    }
    if (sortField === 'lastActive') {
      const aT = new Date(a.lastActive).getTime() || 0;
      const bT = new Date(b.lastActive).getTime() || 0;
      return sortDirection === 'asc' ? aT - bT : bT - aT;
    }
    const fieldMap = {
      name: (x) => x.name,
      contact: (x) => x.email,
      role: (x) => x.displayRole,
      status: (x) => x.status,
    };
    const aV = (fieldMap[sortField]?.(a) || '').toString().toLowerCase();
    const bV = (fieldMap[sortField]?.(b) || '').toString().toLowerCase();
    if (aV < bV) return sortDirection === 'asc' ? -1 : 1;
    if (aV > bV) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  useEffect(() => {
    if (!editingAdmin) {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        countryCode: '',
        countryId: null,
        gender: '',
        role: '',
        password: '',
        permissions: []
      });
      setCountrySearch('');
      return;
    }

    // Wait until countries are loaded
    if (countries.length === 0) return;

    const admin = admins.find(a => a.id === editingAdmin);
    if (admin) {
      const matched = countries.find(c => c.countryPhoneCode === admin.countryCode);
      setFormData({
        firstName: admin.firstName,
        lastName: admin.lastName,
        email: admin.email,
        phone: admin.phone,
        countryCode: admin.countryCode,
        countryId: matched?.countryId || null,
        gender: admin.gender,
        role: admin.role,
        password: '',
        permissions: admin.permissions
      });
      setCountrySearch(matched ? `${matched.isoCode} ${matched.countryPhoneCode}` : (admin.countryCode || ''));
    }
  }, [editingAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'role' && roles.some(r => r.roleName === value)) {
      setFormData(prev => ({ ...prev, role: value, permissions: PERMISSIONS.map(p => p.id) }));
    }

    // Auto-set countryId from selected countryCode
    if (name === 'countryCode') {
      const matched = countries.find(c => c.countryPhoneCode === value);
      setFormData(prev => ({
        ...prev,
        countryCode: value,
        countryId: matched ? matched.countryId : null
      }));
    }
  };

  const handlePermissionChange = (permissionId) => {
    setFormData(prev => {
      const newPermissions = prev.permissions.includes(permissionId)
        ? prev.permissions.filter(p => p !== permissionId)
        : [...prev.permissions, permissionId];
      
      return {
        ...prev,
        permissions: newPermissions
      };
    });
  };

  // Looks up the numeric roleId for a roleName using the roles fetched from the API
  const getRoleId = (roleName) => roles.find(r => r.roleName === roleName)?.roleId;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    const authToken = localStorage.getItem('adminAuthToken');
    const loggedInRoleId = Number(localStorage.getItem('adminRoleId'));

    // Only superadmin (roleId: 1) can create new accounts
    if (!editingAdmin && loggedInRoleId !== 1) {
      setFormError('Only Super Admin can create new administrator accounts.');
      return;
    }

    if (!editingAdmin) {
      // ── CREATE via API ──
      setSubmitLoading(true);
      try {
        const payload = {
          countryId: formData.countryId,
          mobileNo: formData.phone,
          firstName: formData.firstName,
          lastName: formData.lastName,
          gender: formData.gender,
          email: formData.email,
          password: formData.password,
          roleId: getRoleId(formData.role)
        };

        const response = await fetch('https://car.amar.co.jp/api/authorization/auth/admin-create-account', {
          method: 'POST',
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify(payload)
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to create admin');

        await fetchAdmins(); // refresh list
        setShowModal(false);
        setEditingAdmin(null);
        setSuccessMessage(data.message || 'Admin account created successfully.');
        setTimeout(() => setSuccessMessage(null), 4000);
      } catch (err) {
        setFormError(err.message || 'Something went wrong. Please try again.');
        console.error('Error creating admin:', err);
      } finally {
        setSubmitLoading(false);
      }
    } else {
      // ── EDIT via API: PUT /admin-user/update-admin-user/{id} ──
      setSubmitLoading(true);
      try {
        const payload = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          gender: formData.gender,
          userEmailId: formData.email,
          userMobileNumber: formData.phone,
          countryId: formData.countryId,
          roleId: getRoleId(formData.role),
        };

        const response = await fetch(`https://car.amar.co.jp/api/authorization/admin-user/update-admin-user/${editingAdmin}`, {
          method: 'PUT',
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify(payload)
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Failed to update admin');

        await fetchAdmins(); // refresh list with latest data
        setShowModal(false);
        setEditingAdmin(null);
        setSuccessMessage(data.message || 'Admin updated successfully.');
        setTimeout(() => setSuccessMessage(null), 4000);
      } catch (err) {
        setFormError(err.message || 'Something went wrong. Please try again.');
        console.error('Error updating admin:', err);
      } finally {
        setSubmitLoading(false);
      }
    }
  };

  const handleDeleteAdmin = async (id) => {
    const loggedInRoleId  = Number(localStorage.getItem('adminRoleId'));
    const loggedInUserId  = Number(localStorage.getItem('adminUserId'));
    const authToken       = localStorage.getItem('adminAuthToken');

    // Only SUPER_ADMIN (roleId 1) can delete
    if (loggedInRoleId !== 1) return;
    // Cannot delete yourself
    if (id === loggedInUserId) return;

    setDeleteLoading(true);
    try {
      const response = await fetch(
        `https://car.amar.co.jp/api/authorization/admin-user/delete-admin-user/${id}`,
        {
          method: 'DELETE',
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en',
            'Authorization': `Bearer ${authToken}`
          }
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to delete admin');

      await fetchAdmins();
      setDeleteConfirmId(null);
      setSuccessMessage(data.message || 'Admin deleted successfully.');
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      setSuccessMessage(null);
      alert(err.message || 'Something went wrong while deleting.');
      console.error('Delete error:', err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const formatLastActive = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else {
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    }
  };

  const getPermissionCount = (permissions) => {
    return permissions.length;
  };

  const getAvatarColor = (id) => {
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', 
      '#98D8C8', '#F06292', '#7986CB', '#9575CD'
    ];
    return colors[id % colors.length];
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        fontSize: '18px',
        color: '#666'
      }}>
          <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: 2
        }}
      >
        <SpinningLogo src="/image/footer-logo-small.svg" alt="Loading" />
        <Typography variant="body1">Loading car data...</Typography>
      </Box>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        fontSize: '18px',
        color: '#e74c3c'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <div>Error: {error}</div>
          <button 
            onClick={fetchAdmins}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              background: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'system-ui, -apple-system, sans-serif', padding: '20px' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <h2 style={{ margin: '0 0 20px 0', fontSize: '1.4rem', fontWeight: 700, color: '#0f172a' }}>
              👑 Administrator Management
              <span style={{ display: 'block', width: 36, height: 3, background: '#e11d48', borderRadius: 2, marginTop: 6 }}></span>
            </h2>
          </div>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Search admins..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  padding: '10px 40px 10px 15px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px',
                  width: '250px'
                }}
              />
              <span style={{ position: 'absolute', right: '15px', top: '50%', transform: 'translateY(-50%)' }}>🔍</span>
            </div>
            <button
              onClick={() => {
                setEditingAdmin(null);
                setCountrySearch('');
                setShowCountryDropdown(false);
                setFormError(null);
                setShowModal(true);
              }}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span style={{ fontSize: '18px' }}>+</span>
              <span>Add New Admin</span>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '10px', borderBottom: '2px solid #f0f0f0' }}>
          {['all', 'active', 'inactive'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '12px 24px',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === tab ? '2px solid #667eea' : '2px solid transparent',
                color: activeTab === tab ? '#667eea' : '#666',
                fontWeight: activeTab === tab ? '600' : '400',
                cursor: 'pointer',
                fontSize: '14px',
                marginBottom: '-2px'
              }}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)} {tab === 'all' && `(${admins.length})`}
            </button>
          ))}
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div style={{
          marginBottom: '16px',
          padding: '12px 18px',
          background: '#d3f9d8',
          border: '1px solid #8ce99a',
          borderRadius: '8px',
          color: '#2b8a3e',
          fontSize: '14px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          ✅ {successMessage}
        </div>
      )}

      {/* Table */}
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        {filteredAdmins.length > 0 ? (
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '70vh' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr style={{ backgroundColor: '#d32f2f' }}>
                <th onClick={() => handleSort('id')} style={{ padding: '16px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: 'white', backgroundColor: '#d32f2f', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>ID{getSortIcon('id')}</th>
                <th onClick={() => handleSort('name')} style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: 'white', backgroundColor: '#d32f2f', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Admin{getSortIcon('name')}</th>
                <th onClick={() => handleSort('contact')} style={{ padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: 'white', backgroundColor: '#d32f2f', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Contact{getSortIcon('contact')}</th>
                <th onClick={() => handleSort('role')} style={{ padding: '16px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: 'white', backgroundColor: '#d32f2f', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Role & Permissions{getSortIcon('role')}</th>
                <th onClick={() => handleSort('lastActive')} style={{ padding: '16px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: 'white', backgroundColor: '#d32f2f', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Last Active{getSortIcon('lastActive')}</th>
                <th onClick={() => handleSort('status')} style={{ padding: '16px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: 'white', backgroundColor: '#d32f2f', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Status{getSortIcon('status')}</th>
                <th style={{ padding: '16px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: 'white', backgroundColor: '#d32f2f' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedFilteredAdmins.map(admin => (
                <tr key={admin.id} style={{ borderBottom: '1px solid #e9ecef' }}>
                  <td style={{ padding: '16px', fontSize: '14px', color: '#666', textAlign: 'center' }}>#{admin.id.toString().padStart(3, '0')}</td>
                  <td style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: getAvatarColor(admin.id),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '14px'
                      }}>
                        {getInitials(admin.name)}
                      </div>
                      <div>
                        <div style={{ fontWeight: '500', fontSize: '14px', color: '#212529' }}>{admin.name}</div>
                        <div style={{ fontSize: '12px', color: '#868e96' }}>{admin.gender}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <div style={{ fontSize: '14px', color: '#212529' }}>{admin.email}</div>
                    <div style={{ fontSize: '12px', color: '#868e96' }}>{admin.countryCode} {admin.phone}</div>
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '500',
                        background: admin.isSuperAdmin ? '#e7f5ff' : '#f3f0ff',
                        color: admin.isSuperAdmin ? '#1971c2' : '#5f3dc4'
                      }}>
                        {admin.displayRole}
                      </span>
                      <div style={{ position: 'relative' }}>
                        <div style={{
                          padding: '4px 10px',
                          borderRadius: '20px',
                          fontSize: '11px',
                          background: '#f8f9fa',
                          color: '#495057',
                          cursor: 'pointer',
                          border: '1px solid #dee2e6'
                        }}>
                          {getPermissionCount(admin.permissions)} Permissions
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '16px', fontSize: '13px', color: '#666', textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                      <span>⏱</span>
                      {formatLastActive(admin.lastActive)}
                    </div>
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '500',
                      background: admin.status === 'active' ? '#d3f9d8' : '#ffe3e3',
                      color: admin.status === 'active' ? '#2b8a3e' : '#c92a2a'
                    }}>
                      <span style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: admin.status === 'active' ? '#2b8a3e' : '#c92a2a'
                      }}></span>
                      {admin.status.charAt(0).toUpperCase() + admin.status.slice(1)}
                    </div>
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <button
                        onClick={() => {
                          setEditingAdmin(admin.id);
                          setShowCountryDropdown(false);
                          setFormError(null);
                          setShowModal(true);
                        }}
                        style={{
                          padding: '6px 12px',
                          background: '#e7f5ff',
                          color: '#1971c2',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '13px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        ✏️ Edit
                      </button>
                      {(() => {
                        const loggedInRoleId = Number(localStorage.getItem('adminRoleId'));
                        const loggedInUserId = Number(localStorage.getItem('adminUserId'));
                        const canDelete = loggedInRoleId === 1 && admin.id !== loggedInUserId;

                        if (!canDelete) {
                          return (
                            <button
                              disabled
                              title={loggedInRoleId !== 1 ? 'Only Super Admin can delete' : 'Cannot delete yourself'}
                              style={{
                                padding: '6px 12px',
                                background: '#f1f3f5',
                                color: '#adb5bd',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '13px',
                                cursor: 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              🗑️ Delete
                            </button>
                          );
                        }

                        return (
                          <button
                            onClick={() => setDeleteConfirmId(admin.id)}
                            style={{
                              padding: '6px 12px',
                              background: '#fff5f5',
                              color: '#c92a2a',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '13px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            🗑️ Delete
                          </button>
                        );
                      })()}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
            <h3 style={{ fontSize: '18px', fontWeight: '500', marginBottom: '8px' }}>No administrators found</h3>
            <p style={{ color: '#666', fontSize: '14px' }}>Try adjusting your search or filter criteria</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', padding: '0 10px' }}>
        <div style={{ fontSize: '14px', color: '#666' }}>
          Showing {filteredAdmins.length} of {admins.length} administrators
        </div>
        <div style={{ display: 'flex', gap: '5px' }}>
          {[1, 2, 3].map(page => (
            <button
              key={page}
              style={{
                padding: '8px 12px',
                border: '1px solid #dee2e6',
                background: page === 1 ? '#667eea' : 'white',
                color: page === 1 ? 'white' : '#495057',
                borderRadius: '6px',
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              {page}
            </button>
          ))}
        </div>
      </div>

      {/* Modal */}
      {/* ── Delete Confirmation Modal ── */}
      {deleteConfirmId && (() => {
        const adminToDelete = admins.find(a => a.id === deleteConfirmId);
        return (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1100
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              width: '90%',
              maxWidth: '400px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              overflow: 'hidden',
              animation: 'slideUp 0.25s ease'
            }}>
              {/* Red top bar */}
              <div style={{
                background: 'linear-gradient(135deg, #e53e3e, #c53030)',
                padding: '24px',
                display: 'flex',
                justifyContent: 'center'
              }}>
                <div style={{
                  width: '56px', height: '56px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '26px'
                }}>
                  🗑️
                </div>
              </div>

              {/* Body */}
              <div style={{ padding: '28px 28px 24px', textAlign: 'center' }}>
                <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#1a202c', margin: '0 0 10px' }}>
                  Delete Administrator
                </h3>
                <p style={{ fontSize: '14px', color: '#718096', margin: '0 0 6px', lineHeight: '1.6' }}>
                  Are you sure you want to delete
                </p>
                <p style={{ fontSize: '16px', fontWeight: '700', color: '#c53030', margin: '0 0 20px' }}>
                  {adminToDelete?.name}?
                </p>
                <p style={{ fontSize: '12px', color: '#a0aec0', margin: '0 0 28px' }}>
                  This action cannot be undone.
                </p>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  <button
                    onClick={() => setDeleteConfirmId(null)}
                    disabled={deleteLoading}
                    style={{
                      padding: '10px 28px',
                      border: '1.5px solid #e2e8f0',
                      background: 'white',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      color: '#4a5568',
                      minWidth: '110px'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDeleteAdmin(deleteConfirmId)}
                    disabled={deleteLoading}
                    style={{
                      padding: '10px 28px',
                      background: deleteLoading ? '#fc8181' : '#e53e3e',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: deleteLoading ? 'not-allowed' : 'pointer',
                      color: 'white',
                      minWidth: '110px',
                      transition: 'background 0.2s'
                    }}
                  >
                    {deleteLoading ? 'Deleting...' : 'Yes, Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #e9ecef',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>
                {editingAdmin ? '✏️ Edit Administrator' : '➕ Add New Administrator'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingAdmin(null);
                  setFormError(null);
                  setCountrySearch('');
                  setShowCountryDropdown(false);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#868e96'
                }}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px', color: '#495057' }}>
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #dee2e6',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px', color: '#495057' }}>
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #dee2e6',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px', color: '#495057' }}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    autoComplete="off"
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #dee2e6',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px', color: '#495057' }}>
                    Phone Number
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <div style={{ position: 'relative', width: '150px' }}>
                      <input
                        type="text"
                        placeholder={formData.countryCode ? (countries.find(c => c.countryPhoneCode === formData.countryCode)?.isoCode + ' ' + formData.countryCode) : '+Code'}
                        value={countrySearch}
                        onChange={(e) => {
                          setCountrySearch(e.target.value);
                          setShowCountryDropdown(true);
                        }}
                        onFocus={() => setShowCountryDropdown(true)}
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #dee2e6',
                          borderRadius: '6px',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                      />
                      {showCountryDropdown && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          background: 'white',
                          border: '1px solid #dee2e6',
                          borderRadius: '6px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 999,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}>
                          {countries
                            .filter(c =>
                              c.countryPhoneCode.includes(countrySearch) ||
                              c.isoCode.toLowerCase().includes(countrySearch.toLowerCase()) ||
                              c.countryName.toLowerCase().includes(countrySearch.toLowerCase())
                            )
                            .map(c => (
                              <div
                                key={c.countryId}
                                onClick={() => {
                                  setFormData(prev => ({
                                    ...prev,
                                    countryCode: c.countryPhoneCode,
                                    countryId: c.countryId
                                  }));
                                  setCountrySearch(c.isoCode + ' ' + c.countryPhoneCode); // input me isoCode + phoneCode
                                  setShowCountryDropdown(false);
                                }}
                                style={{
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                  borderBottom: '1px solid #f0f0f0',
                                  background: formData.countryCode === c.countryPhoneCode ? '#e7f5ff' : 'white',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
                                onMouseLeave={e => e.currentTarget.style.background = formData.countryCode === c.countryPhoneCode ? '#e7f5ff' : 'white'}
                              >
                                <span style={{ fontWeight: '600', color: '#495057', fontSize: '12px', width: '36px', flexShrink: 0 }}>{c.isoCode}</span>
                                <span style={{ color: '#333', fontSize: '12px' }}>{c.countryPhoneCode}</span>
                              </div>
                            ))
                          }
                        </div>
                      )}
                    </div>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Mobile Number"
                      style={{
                        flex: 1,
                        padding: '10px',
                        border: '1px solid #dee2e6',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px', color: '#495057' }}>
                    Gender
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #dee2e6',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px', color: '#495057' }}>
                    Role *
                  </label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #dee2e6',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select Role</option>
                    {roles.map(r => (
                      <option key={r.roleId} value={r.roleName}>{r.displayName}</option>
                    ))}
                  </select>
                </div>
              </div>

              {!editingAdmin && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '6px', color: '#495057' }}>
                    Password *
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    autoComplete="new-password"
                    required={!editingAdmin}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #dee2e6',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              )}

              {/* <div style={{ marginBottom: '24px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#495057' }}>Permissions</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  {PERMISSIONS.map(permission => (
                    <div
                      key={permission.id}
                      onClick={() => handlePermissionChange(permission.id)}
                      style={{
                        padding: '12px',
                        border: '1px solid #dee2e6',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        background: formData.permissions.includes(permission.id) ? '#e7f5ff' : 'white',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}
                    >
                      <div style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '4px',
                        border: '2px solid #1971c2',
                        background: formData.permissions.includes(permission.id) ? '#1971c2' : 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '12px'
                      }}>
                        {formData.permissions.includes(permission.id) ? '✓' : ''}
                      </div>
                      <span style={{ fontSize: '13px', color: '#495057' }}>{permission.label}</span>
                    </div>
                  ))}
                </div>
              </div> */}

              {formError && (
                <div style={{
                  marginBottom: '16px',
                  padding: '10px 14px',
                  background: '#ffe3e3',
                  border: '1px solid #ffc9c9',
                  borderRadius: '6px',
                  color: '#c92a2a',
                  fontSize: '13px'
                }}>
                  ⚠️ {formError}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingAdmin(null);
                    setFormError(null);
                    setCountrySearch('');
                    setShowCountryDropdown(false);
                  }}
                  style={{
                    padding: '10px 24px',
                    border: '1px solid #dee2e6',
                    background: 'white',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitLoading}
                  style={{
                    padding: '10px 24px',
                    background: submitLoading ? '#adb5bd' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: submitLoading ? 'not-allowed' : 'pointer',
                    fontWeight: '500'
                  }}
                >
                  {editingAdmin ? '✏️ Update Admin' : submitLoading ? 'Creating...' : '👑 Create Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllAdmins;